<template>
  <div class="commission-management">
    <div class="header">
      <h2>商户返佣管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleCreateCommission">
          <el-icon><Plus /></el-icon>
          创建返佣规则
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="商户名称">
          <el-input v-model="searchForm.merchantName" placeholder="请输入商户名称" clearable />
        </el-form-item>
        <el-form-item label="返佣类型">
          <el-select v-model="searchForm.commissionType" placeholder="请选择返佣类型" clearable>
            <el-option label="固定比例" value="fixed_rate" />
            <el-option label="阶梯比例" value="tiered_rate" />
            <el-option label="固定金额" value="fixed_amount" />
            <el-option label="活动返佣" value="promotion" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="启用" value="active" />
            <el-option label="停用" value="inactive" />
            <el-option label="审核中" value="pending" />
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>今日返佣</h3>
              <p class="stat-value">¥{{ formatAmount(stats.todayCommission) }}</p>
              <p class="stat-label">{{ stats.todayCount }}笔返佣</p>
            </div>
            <el-icon class="stat-icon" color="#409eff"><Money /></el-icon>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>本月返佣</h3>
              <p class="stat-value">¥{{ formatAmount(stats.monthCommission) }}</p>
              <p class="stat-label">{{ stats.monthCount }}笔返佣</p>
            </div>
            <el-icon class="stat-icon" color="#67c23a"><TrendCharts /></el-icon>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>待结算</h3>
              <p class="stat-value">¥{{ formatAmount(stats.pendingAmount) }}</p>
              <p class="stat-label">{{ stats.pendingCount }}笔待结</p>
            </div>
            <el-icon class="stat-icon" color="#e6a23c"><Clock /></el-icon>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-item">
              <h3>商户总数</h3>
              <p class="stat-value">{{ stats.merchantCount }}</p>
              <p class="stat-label">参与返佣商户</p>
            </div>
            <el-icon class="stat-icon" color="#f56c6c"><Shop /></el-icon>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 返佣规则列表 -->
    <el-card>
      <el-table :data="commissionList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="规则ID" width="100" />
        <el-table-column label="商户信息" width="200">
          <template #default="{ row }">
            <div class="merchant-info">
              <div class="merchant-name">{{ row.merchantName }}</div>
              <div class="merchant-id">ID: {{ row.merchantId }}</div>
              <div class="merchant-contact">{{ row.merchantContact || '暂无联系人' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="productName" label="适用商品" width="150" show-overflow-tooltip />
        <el-table-column label="返佣规则" width="200">
          <template #default="{ row }">
            <div class="commission-rule">
              <div v-if="row.commissionType === 'fixed_rate'">
                <span class="rule-label">固定比例：</span>
                <span class="rule-value">{{ row.commissionRate }}%</span>
              </div>
              <div v-if="row.commissionType === 'fixed_amount'">
                <span class="rule-label">固定金额：</span>
                <span class="rule-value">¥{{ formatAmount(row.commissionAmount) }}</span>
              </div>
              <div v-if="row.commissionType === 'tiered_rate'">
                <span class="rule-label">阶梯比例：</span>
                <span class="rule-value">{{ row.tieredRates }}%</span>
              </div>
              <div v-if="row.commissionType === 'promotion'">
                <span class="rule-label">活动返佣：</span>
                <span class="rule-value">{{ row.promotionName }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="返佣周期" width="150">
          <template #default="{ row }">
            <div class="commission-cycle">
              <div>{{ getCommissionCycleText(row.commissionCycle) }}</div>
              <div v-if="row.settlementDay" class="settlement-day">
                结算日：每月{{ row.settlementDay }}日
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="minAmount" label="最低消费" width="120">
          <template #default="{ row }">
            <span v-if="row.minAmount">¥{{ formatAmount(row.minAmount) }}</span>
            <span v-else class="no-limit">不限</span>
          </template>
        </el-table-column>
        <el-table-column prop="maxAmount" label="最高消费" width="120">
          <template #default="{ row }">
            <span v-if="row.maxAmount">¥{{ formatAmount(row.maxAmount) }}</span>
            <span v-else class="no-limit">不限</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleViewDetails(row)">
              详情
            </el-button>
            <el-button type="warning" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button 
              v-if="row.status === 'active'" 
              type="danger" 
              size="small" 
              @click="handleDeactivate(row)"
            >
              停用
            </el-button>
            <el-button 
              v-else-if="row.status === 'inactive'" 
              type="success" 
              size="small" 
              @click="handleActivate(row)"
            >
              启用
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑返佣规则对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="resetForm"
    >
      <el-form ref="commissionFormRef" :model="commissionForm" :rules="commissionRules" label-width="120px">
        <el-form-item label="商户" prop="merchantId">
          <el-select 
            v-model="commissionForm.merchantId" 
            placeholder="请选择商户"
            filterable
            remote
            :remote-method="searchMerchants"
            :loading="merchantLoading"
          >
            <el-option
              v-for="merchant in merchantOptions"
              :key="merchant.id"
              :label="`${merchant.name} (ID: ${merchant.id})`"
              :value="merchant.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="适用商品" prop="productName">
          <el-input 
            v-model="commissionForm.productName" 
            placeholder="请输入适用商品名称，留空表示所有商品"
          />
        </el-form-item>
        <el-form-item label="返佣类型" prop="commissionType">
          <el-select v-model="commissionForm.commissionType" placeholder="请选择返佣类型">
            <el-option label="固定比例" value="fixed_rate" />
            <el-option label="阶梯比例" value="tiered_rate" />
            <el-option label="固定金额" value="fixed_amount" />
            <el-option label="活动返佣" value="promotion" />
          </el-select>
        </el-form-item>

        <!-- 固定比例 -->
        <el-form-item v-if="commissionForm.commissionType === 'fixed_rate'" label="返佣比例" prop="commissionRate">
          <el-input 
            v-model="commissionForm.commissionRate" 
            placeholder="请输入返佣比例"
            type="number"
            :min="0"
            :max="100"
            step="0.01"
          >
            <template #append>%</template>
          </el-input>
        </el-form-item>

        <!-- 固定金额 -->
        <el-form-item v-if="commissionForm.commissionType === 'fixed_amount'" label="返佣金额" prop="commissionAmount">
          <el-input 
            v-model="commissionForm.commissionAmount" 
            placeholder="请输入返佣金额"
            type="number"
            :min="0.01"
            step="0.01"
          >
            <template #append>元</template>
          </el-input>
        </el-form-item>

        <!-- 阶梯比例 -->
        <el-form-item v-if="commissionForm.commissionType === 'tiered_rate'" label="阶梯规则" prop="tieredRates">
          <el-input 
            v-model="commissionForm.tieredRates" 
            type="textarea"
            :rows="3"
            placeholder="请输入阶梯规则，格式：0-1000:5%, 1001-5000:8%, 5001+:10%"
          />
        </el-form-item>

        <!-- 活动返佣 -->
        <el-form-item v-if="commissionForm.commissionType === 'promotion'" label="活动名称" prop="promotionName">
          <el-input 
            v-model="commissionForm.promotionName" 
            placeholder="请输入活动名称"
          />
        </el-form-item>

        <el-form-item label="返佣周期" prop="commissionCycle">
          <el-select v-model="commissionForm.commissionCycle" placeholder="请选择返佣周期">
            <el-option label="实时返佣" value="real_time" />
            <el-option label="每日结算" value="daily" />
            <el-option label="每周结算" value="weekly" />
            <el-option label="每月结算" value="monthly" />
            <el-option label="每季结算" value="quarterly" />
          </el-select>
        </el-form-item>

        <el-form-item v-if="commissionForm.commissionCycle === 'monthly'" label="结算日" prop="settlementDay">
          <el-input-number 
            v-model="commissionForm.settlementDay" 
            :min="1" 
            :max="28" 
            placeholder="请输入结算日"
          />
        </el-form-item>

        <el-form-item label="消费限额">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item prop="minAmount">
                <template #label>
                  <span>最低消费</span>
                </template>
                <el-input 
                  v-model="commissionForm.minAmount" 
                  placeholder="请输入最低消费金额"
                  type="number"
                  :min="0"
                  step="0.01"
                >
                  <template #append>元</template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="maxAmount">
                <template #label>
                  <span>最高消费</span>
                </template>
                <el-input 
                  v-model="commissionForm.maxAmount" 
                  placeholder="请输入最高消费金额"
                  type="number"
                  :min="0"
                  step="0.01"
                >
                  <template #append>元</template>
                </el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-select v-model="commissionForm.status" placeholder="请选择状态">
            <el-option label="启用" value="active" />
            <el-option label="停用" value="inactive" />
            <el-option label="审核中" value="pending" />
          </el-select>
        </el-form-item>

        <el-form-item label="生效时间" prop="effectiveDate">
          <el-date-picker
            v-model="commissionForm.effectiveDate"
            type="date"
            placeholder="请选择生效日期"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>

        <el-form-item label="失效时间" prop="expiryDate">
          <el-date-picker
            v-model="commissionForm.expiryDate"
            type="date"
            placeholder="请选择失效日期"
            value-format="YYYY-MM-DD"
          />
        </el-form-item>

        <el-form-item label="描述" prop="description">
          <el-input 
            v-model="commissionForm.description" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入返佣规则描述"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 查看详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="返佣规则详情"
      width="700px"
    >
      <el-descriptions :column="2" border v-if="currentCommission">
        <el-descriptions-item label="规则ID">{{ currentCommission.id }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusTag(currentCommission.status)">
            {{ getStatusText(currentCommission.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="商户名称">{{ currentCommission.merchantName }}</el-descriptions-item>
        <el-descriptions-item label="商户ID">{{ currentCommission.merchantId }}</el-descriptions-item>
        <el-descriptions-item label="商户联系人">{{ currentCommission.merchantContact || '暂无' }}</el-descriptions-item>
        <el-descriptions-item label="适用商品">{{ currentCommission.productName || '所有商品' }}</el-descriptions-item>
        <el-descriptions-item label="返佣类型">{{ getCommissionTypeText(currentCommission.commissionType) }}</el-descriptions-item>
        <el-descriptions-item label="返佣规则">
          <div v-if="currentCommission.commissionType === 'fixed_rate'">
            固定比例：{{ currentCommission.commissionRate }}%
          </div>
          <div v-else-if="currentCommission.commissionType === 'fixed_amount'">
            固定金额：¥{{ formatAmount(currentCommission.commissionAmount) }}
          </div>
          <div v-else-if="currentCommission.commissionType === 'tiered_rate'">
            阶梯比例：{{ currentCommission.tieredRates }}
          </div>
          <div v-else-if="currentCommission.commissionType === 'promotion'">
            活动名称：{{ currentCommission.promotionName }}
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="返佣周期">{{ getCommissionCycleText(currentCommission.commissionCycle) }}</el-descriptions-item>
        <el-descriptions-item label="结算日">
          <span v-if="currentCommission.settlementDay">每月{{ currentCommission.settlementDay }}日</span>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="最低消费">
          <span v-if="currentCommission.minAmount">¥{{ formatAmount(currentCommission.minAmount) }}</span>
          <span v-else>不限</span>
        </el-descriptions-item>
        <el-descriptions-item label="最高消费">
          <span v-if="currentCommission.maxAmount">¥{{ formatAmount(currentCommission.maxAmount) }}</span>
          <span v-else>不限</span>
        </el-descriptions-item>
        <el-descriptions-item label="生效日期">{{ currentCommission.effectiveDate || '-' }}</el-descriptions-item>
        <el-descriptions-item label="失效日期">{{ currentCommission.expiryDate || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(currentCommission.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ formatDate(currentCommission.updatedAt) || '-' }}</el-descriptions-item>
        <el-descriptions-item label="描述" :span="2">{{ currentCommission.description || '无' }}</el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleEdit(currentCommission); detailDialogVisible = false">编辑</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Plus, 
  Refresh, 
  Search,
  Money,
  TrendCharts,
  Clock,
  Shop
} from '@element-plus/icons-vue'

// 状态管理
const loading = ref(false)
const merchantLoading = ref(false)
const dialogVisible = ref(false)
const detailDialogVisible = ref(false)
const isEditMode = ref(false)
const submitting = ref(false)
const commissionFormRef = ref()
const currentCommission = ref(null)

// 搜索表单
const searchForm = reactive({
  merchantName: '',
  commissionType: '',
  status: '',
  dateRange: []
})

// 商户选项
const merchantOptions = ref([
  { id: 1, name: '天猫超市' },
  { id: 2, name: '京东商城' },
  { id: 3, name: '美团外卖' },
  { id: 4, name: '饿了么' },
  { id: 5, name: '滴滴出行' }
])

// 返佣表单
const commissionForm = reactive({
  merchantId: '',
  productName: '',
  commissionType: 'fixed_rate',
  commissionRate: '',
  commissionAmount: '',
  tieredRates: '',
  promotionName: '',
  commissionCycle: 'monthly',
  settlementDay: 15,
  minAmount: '',
  maxAmount: '',
  status: 'active',
  effectiveDate: '',
  expiryDate: '',
  description: ''
})

// 表单验证规则
const commissionRules = {
  merchantId: [
    { required: true, message: '请选择商户', trigger: 'change' }
  ],
  commissionType: [
    { required: true, message: '请选择返佣类型', trigger: 'change' }
  ],
  commissionRate: [
    { 
      required: computed(() => commissionForm.commissionType === 'fixed_rate'), 
      message: '请输入返佣比例', 
      trigger: 'blur' 
    },
    { 
      pattern: /^\d+(\.\d{1,2})?$/, 
      message: '请输入正确的比例格式', 
      trigger: 'blur' 
    }
  ],
  commissionAmount: [
    { 
      required: computed(() => commissionForm.commissionType === 'fixed_amount'), 
      message: '请输入返佣金额', 
      trigger: 'blur' 
    },
    { 
      pattern: /^\d+(\.\d{1,2})?$/, 
      message: '请输入正确的金额格式', 
      trigger: 'blur' 
    }
  ],
  tieredRates: [
    { 
      required: computed(() => commissionForm.commissionType === 'tiered_rate'), 
      message: '请输入阶梯规则', 
      trigger: 'blur' 
    }
  ],
  promotionName: [
    { 
      required: computed(() => commissionForm.commissionType === 'promotion'), 
      message: '请输入活动名称', 
      trigger: 'blur' 
    }
  ],
  commissionCycle: [
    { required: true, message: '请选择返佣周期', trigger: 'change' }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ],
  effectiveDate: [
    { required: true, message: '请选择生效日期', trigger: 'change' }
  ]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 统计信息
const stats = reactive({
  todayCommission: 12500.50,
  todayCount: 42,
  monthCommission: 285000.75,
  monthCount: 856,
  pendingAmount: 15000.00,
  pendingCount: 25,
  merchantCount: 58
})

// 返佣规则列表数据（模拟数据）
const commissionList = ref([
  {
    id: 1,
    merchantId: 1,
    merchantName: '天猫超市',
    merchantContact: '张三 13800000001',
    productName: '所有商品',
    commissionType: 'fixed_rate',
    commissionRate: 5,
    commissionCycle: 'monthly',
    settlementDay: 15,
    minAmount: 0,
    maxAmount: null,
    status: 'active',
    effectiveDate: '2026-01-01',
    expiryDate: '2026-12-31',
    description: '天猫超市所有商品5%返佣',
    createdAt: '2026-01-01T10:00:00',
    updatedAt: '2026-01-01T10:00:00'
  },
  {
    id: 2,
    merchantId: 2,
    merchantName: '京东商城',
    merchantContact: '李四 13800000002',
    productName: '电子产品',
    commissionType: 'tiered_rate',
    tieredRates: '0-1000:5%, 1001-5000:8%, 5001+:10%',
    commissionCycle: 'monthly',
    settlementDay: 20,
    minAmount: 100,
    maxAmount: 10000,
    status: 'active',
    effectiveDate: '2026-01-01',
    expiryDate: '2026-06-30',
    description: '京东商城电子产品阶梯返佣',
    createdAt: '2026-01-01T11:00:00',
    updatedAt: '2026-01-01T11:00:00'
  },
  {
    id: 3,
    merchantId: 3,
    merchantName: '美团外卖',
    merchantContact: '王五 13800000003',
    productName: '外卖订单',
    commissionType: 'fixed_amount',
    commissionAmount: 2,
    commissionCycle: 'real_time',
    minAmount: 20,
    maxAmount: null,
    status: 'active',
    effectiveDate: '2026-02-01',
    expiryDate: '2026-12-31',
    description: '美团外卖每单固定2元返佣',
    createdAt: '2026-02-01T09:00:00',
    updatedAt: '2026-02-01T09:00:00'
  },
  {
    id: 4,
    merchantId: 4,
    merchantName: '饿了么',
    merchantContact: '赵六 13800000004',
    productName: '所有订单',
    commissionType: 'promotion',
    promotionName: '春季大促',
    commissionCycle: 'weekly',
    minAmount: 30,
    maxAmount: null,
    status: 'pending',
    effectiveDate: '2026-03-01',
    expiryDate: '2026-03-31',
    description: '饿了么春季大促活动返佣',
    createdAt: '2026-02-28T14:00:00',
    updatedAt: '2026-02-28T14:00:00'
  }
])

// 计算属性
const dialogTitle = computed(() => {
  return isEditMode.value ? '编辑返佣规则' : '创建返佣规则'
})

// 方法
const getStatusText = (status) => {
  const map = {
    active: '启用',
    inactive: '停用',
    pending: '审核中'
  }
  return map[status] || status
}

const getStatusTag = (status) => {
  const map = {
    active: 'success',
    inactive: 'danger',
    pending: 'warning'
  }
  return map[status] || ''
}

const getCommissionTypeText = (type) => {
  const map = {
    fixed_rate: '固定比例',
    tiered_rate: '阶梯比例',
    fixed_amount: '固定金额',
    promotion: '活动返佣'
  }
  return map[type] || type
}

const getCommissionCycleText = (cycle) => {
  const map = {
    real_time: '实时返佣',
    daily: '每日结算',
    weekly: '每周结算',
    monthly: '每月结算',
    quarterly: '每季结算'
  }
  return map[cycle] || cycle
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toFixed(2)
}

const refreshData = () => {
  loading.value = true
  // 模拟API请求
  setTimeout(() => {
    loading.value = false
    ElMessage.success('数据已刷新')
  }, 500)
}

const handleSearch = () => {
  pagination.current = 1
  refreshData()
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => {
    searchForm[key] = ''
  })
  searchForm.dateRange = []
  handleSearch()
}

const searchMerchants = (query) => {
  if (query) {
    merchantLoading.value = true
    // 模拟搜索商户
    setTimeout(() => {
      merchantLoading.value = false
      merchantOptions.value = [
        { id: 1, name: '天猫超市' },
        { id: 2, name: '京东商城' },
        { id: 3, name: '美团外卖' }
      ].filter(merchant => merchant.name.includes(query))
    }, 500)
  } else {
    merchantOptions.value = []
  }
}

const handleSizeChange = (size) => {
  pagination.size = size
  refreshData()
}

const handleCurrentChange = (page) => {
  pagination.current = page
  refreshData()
}

const handleCreateCommission = () => {
  isEditMode.value = false
  dialogVisible.value = true
}

const handleEdit = (row) => {
  isEditMode.value = true
  Object.assign(commissionForm, row)
  dialogVisible.value = true
}

const handleViewDetails = (row) => {
  currentCommission.value = row
  detailDialogVisible.value = true
}

const handleDeactivate = async (row) => {
  try {
    await ElMessageBox.confirm('确定要停用该返佣规则吗？停用后将不再计算返佣。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    // 模拟API请求
    row.status = 'inactive'
    ElMessage.success('返佣规则已停用')
    refreshData()
  } catch {
    // 用户取消
  }
}

const handleActivate = async (row) => {
  try {
    await ElMessageBox.confirm('确定要启用该返佣规则吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    // 模拟API请求
    row.status = 'active'
    ElMessage.success('返佣规则已启用')
    refreshData()
  } catch {
    // 用户取消
  }
}

const resetForm = () => {
  if (commissionFormRef.value) {
    commissionFormRef.value.resetFields()
  }
  Object.keys(commissionForm).forEach(key => {
    if (key !== 'commissionType' && key !== 'commissionCycle' && key !== 'status') {
      commissionForm[key] = ''
    }
  })
  commissionForm.commissionType = 'fixed_rate'
  commissionForm.commissionCycle = 'monthly'
  commissionForm.status = 'active'
  commissionForm.settlementDay = 15
}

const handleSubmit = async () => {
  if (!commissionFormRef.value) return
  
  try {
    await commissionFormRef.value.validate()
    
    submitting.value = true
    
    // 模拟API请求
    setTimeout(() => {
      submitting.value = false
      dialogVisible.value = false
      
      if (isEditMode.value) {
        // 更新返佣规则
        const index = commissionList.value.findIndex(c => c.id === commissionForm.id)
        if (index !== -1) {
          Object.assign(commissionList.value[index], commissionForm)
        }
        ElMessage.success('返佣规则更新成功')
      } else {
        // 创建返佣规则
        const merchant = merchantOptions.value.find(m => m.id === commissionForm.merchantId)
        const newCommission = {
          id: commissionList.value.length + 1,
          ...commissionForm,
          merchantName: merchant ? merchant.name : '未知商户',
          merchantContact: '联系人 13800000000',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        commissionList.value.unshift(newCommission)
        ElMessage.success('返佣规则创建成功')
      }
      
      resetForm()
      refreshData()
    }, 500)
  } catch (error) {
    console.log('表单验证失败:', error)
  }
}

// 生命周期
onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.commission-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.stat-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-item {
  text-align: left;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin: 5px 0;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.stat-icon {
  font-size: 40px;
  opacity: 0.7;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.merchant-info {
  display: flex;
  flex-direction: column;
}

.merchant-name {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
}

.merchant-id {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
}

.merchant-contact {
  font-size: 12px;
  color: #666;
}

.commission-rule {
  display: flex;
  flex-direction: column;
}

.rule-label {
  font-size: 12px;
  color: #909399;
}

.rule-value {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.commission-cycle {
  display: flex;
  flex-direction: column;
}

.settlement-day {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.no-limit {
  color: #909399;
  font-size: 12px;
}

:deep(.el-date-editor) {
  width: 240px;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}
</style>
